import { useMemo } from 'react';

const MILESTONES = [
  { threshold: 0, message: "Just getting started...", color: "bg-gray-400" },
  { threshold: 100, message: "Warming up nicely 🔥", color: "bg-stone-500" },
  { threshold: 500, message: "Building momentum!", color: "bg-yellow-400" },
  { threshold: 1000, message: "We're getting there...", color: "bg-lime-400" },
  { threshold: 1500, message: "Solid draft forming!", color: "bg-green-400" },
  { threshold: 2000, message: "Chapter looking complete ✓", color: "bg-emerald-500" },
];

const TARGET_WORDS = 2000;

function WordCountBar({ content }) {
  const wordCount = useMemo(() => {
    return content.trim().split(/\s+/).filter(Boolean).length;
  }, [content]);

  const percentage = Math.min((wordCount / TARGET_WORDS) * 100, 100);
  
  const currentMilestone = useMemo(() => {
    let milestone = MILESTONES[0];
    for (const m of MILESTONES) {
      if (wordCount >= m.threshold) {
        milestone = m;
      }
    }
    return milestone;
  }, [wordCount]);

  const canExpand = wordCount >= 1500;

  return (
    <div className="space-y-2" data-testid="word-count-bar">
      {/* Progress Bar */}
      <div className="relative">
        <div className="h-3 bg-gray-200 dark:bg-[#2A2520] rounded-full overflow-hidden">
          <div 
            className={`h-full ${currentMilestone.color} transition-all duration-500 ease-out`}
            style={{ width: `${percentage}%` }}
          />
        </div>
        
        {/* Milestone markers */}
        <div className="absolute inset-0 flex items-center">
          {[500, 1000, 1500, 2000].map((mark) => (
            <div 
              key={mark}
              className="absolute h-3 w-0.5 bg-gray-400/50 dark:bg-gray-500/50"
              style={{ left: `${(mark / TARGET_WORDS) * 100}%` }}
            />
          ))}
        </div>
      </div>

      {/* Stats Row */}
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center space-x-2">
          <span className="font-bold text-[#2A2420] dark:text-[#F0EBE4]">
            {wordCount.toLocaleString()}
          </span>
          <span className="text-[#2A2420]/60 dark:text-[#F0EBE4]/60">
            / {TARGET_WORDS.toLocaleString()} words
          </span>
        </div>
        
        <span className={`font-medium ${
          wordCount >= 1500 
            ? 'text-green-600 dark:text-green-400' 
            : 'text-[#2A2420]/70 dark:text-[#F0EBE4]/70'
        }`}>
          {currentMilestone.message}
        </span>
      </div>

      {/* Expansion hint */}
      {canExpand && (
        <div className="text-xs text-green-600 dark:text-green-400 flex items-center space-x-1">
          <span>✨</span>
          <span>Good draft length! AI can safely help expand thin sections.</span>
        </div>
      )}
    </div>
  );
}

export default WordCountBar;
